/**
 * In-memory StorageDriver. Backs the demo build and the `?local=1` escape hatch.
 * No network, no persistence — state resets on page reload.
 *
 * Every write updates the driver's internal cache and synchronously notifies
 * subscribed handlers. `pushAuditEvent` + `pushAuditSession` append to the `audit`
 * collection via the same path-patch mechanism Firebase uses, so the in-memory
 * model stays byte-for-byte equivalent.
 *
 * Session 1 extraction — no behavior change from the pre-extraction local path.
 */

import { applyPatchLocal, buildInitialData, generateLocalId } from './StorageDriver.js';
import {
  isCampaignSandboxStorageConfig,
  getCampaignSandboxIdFromConfig,
  loadCampaignSandboxState,
  saveCampaignSandboxState,
} from './campaignSandboxStorage.js';
import {
  isSandboxCharacterForSandbox,
  normalizeSandboxStateChars,
} from '../campaignSandboxContract.js';
import {
  applyCharInventoryItemAbilityActivation,
  applyCharInventoryItemAdd,
  applyCharInventoryItemConsume,
  applyCharInventoryItemEquipToggle,
  applyCharInventoryItemFavoriteToggle,
  applyCharInventoryItemRemove,
  applyCharInventoryItemUpdate,
  applyCharInventoryToCraftingTableTransfer,
  applyCharToCharTransfer,
  applyCraftOutputToCharInventory,
  applyCraftOutputToPartyPool,
  applyCraftingTableRowToCharInventory,
  applyCraftingTableRowToPartyPool,
  applyPartyDepositToCharAndPool,
  applyPartyTakeToCharAndPool,
} from '../items/authority.js';

const JOURNAL_VIRTUAL_BASE_PATH = {
  journalRecent: 'journal',
  journalPage: 'journal',
  journalEntry: 'journal',
  dmJournalRecent: 'dmJournal',
  dmJournalPage: 'dmJournal',
  dmJournalEntry: 'dmJournal',
};

const MESSAGE_VIRTUAL_BASE_PATH = {
  messageLiveTail: 'messages',
  messagePanelHistory: 'messages',
  messageUnreadCounts: 'messages',
};

const AUDIT_VIRTUAL_BASE_PATH = {
  auditSessionMeta: 'audit',
  auditMeta: 'audit',
  auditRecent: 'audit',
};

const asPositiveInteger = (value, fallback, max) => {
  const n = Number.isFinite(value) ? Math.trunc(value) : fallback;
  return Math.max(1, Math.min(max, n));
};

const asNonNegativeInteger = (value, fallback, max = Number.MAX_SAFE_INTEGER) => {
  const n = Number.isFinite(value) ? Math.trunc(value) : fallback;
  return Math.max(0, Math.min(max, n));
};

const entryTimestamp = (entry) => {
  if (typeof entry?.updatedAt === 'number') return entry.updatedAt;
  if (typeof entry?.createdAt === 'number') return entry.createdAt;
  const parsed = Date.parse(entry?.updatedAt || entry?.createdAt || '');
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeJournalEntries = (entries, args = {}) => {
  const category = typeof args.category === 'string' && args.category.trim() ? args.category.trim() : null;
  const sessionId = typeof args.sessionId === 'string' && args.sessionId.trim() ? args.sessionId.trim() : null;
  return (Array.isArray(entries) ? entries : [])
    .filter(entry => entry && !entry.deleted && !entry.isDeleted)
    .filter(entry => !category || entry.category === category)
    .filter(entry => !sessionId || entry.sessionId === sessionId)
    .slice()
    .sort((a, b) => entryTimestamp(b) - entryTimestamp(a));
};

const deriveJournalVirtualValue = (state, path, args = {}) => {
  const basePath = JOURNAL_VIRTUAL_BASE_PATH[path];
  const entries = normalizeJournalEntries(state[basePath], args);
  if (path === 'journalRecent' || path === 'dmJournalRecent') {
    const limit = asPositiveInteger(args.limit, 50, 100);
    return entries.slice(0, limit);
  }
  if (path === 'journalPage' || path === 'dmJournalPage') {
    const numItems = asPositiveInteger(args.paginationOpts?.numItems ?? args.numItems, 200, 200);
    const offset = asNonNegativeInteger(Number(args.paginationOpts?.cursor ?? args.cursor ?? 0), 0);
    const page = entries.slice(offset, offset + numItems);
    const nextOffset = offset + page.length;
    return {
      page,
      isDone: nextOffset >= entries.length,
      continueCursor: nextOffset >= entries.length ? null : String(nextOffset),
    };
  }
  if (path === 'journalEntry' || path === 'dmJournalEntry') {
    const entryId = typeof args.entryId === 'string' ? args.entryId.trim() : '';
    if (!entryId) return null;
    return entries.find(entry => entry?.id === entryId) || null;
  }
  return undefined;
};

const messageTimestamp = (message) => {
  if (typeof message?.timestamp === 'number') return message.timestamp;
  const parsed = Date.parse(message?.timestamp || message?.createdAt || '');
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeMessagesState = (messages = {}) => ({
  ...(messages && typeof messages === 'object' ? messages : {}),
  statusUpdates: Array.isArray(messages?.statusUpdates) ? messages.statusUpdates : [],
  dmChats: messages?.dmChats && typeof messages.dmChats === 'object' ? messages.dmChats : {},
});

const statusVisibleToChar = (message, charId) => (
  message
  && !message.deleted
  && (
    !charId
    || message.visibility === 'public'
    || (Array.isArray(message.recipients) && message.recipients.includes(charId))
  )
);

const deriveMessageVirtualValue = (state, path, args = {}) => {
  const messages = normalizeMessagesState(state.messages);
  const charId = typeof args.charId === 'string' && args.charId.trim() ? args.charId.trim() : null;
  const statusLimit = asPositiveInteger(args.statusLimit, path === 'messageLiveTail' ? 50 : 200, 500);
  const dmLimit = asPositiveInteger(args.dmLimit, path === 'messageLiveTail' ? 50 : 200, 500);
  if (path === 'messageUnreadCounts') {
    const counts = {};
    Object.entries(messages.dmChats || {}).forEach(([dmCharId, entries]) => {
      counts[dmCharId] = (Array.isArray(entries) ? entries : []).filter(message => (
        !message.deleted && !message.read && message.from === 'DM'
      )).length;
    });
    return counts;
  }
  const statusUpdates = (messages.statusUpdates || [])
    .filter(message => statusVisibleToChar(message, charId))
    .slice()
    .sort((a, b) => messageTimestamp(b) - messageTimestamp(a))
    .slice(0, statusLimit);
  const dmChats = {};
  if (charId) {
    dmChats[charId] = (Array.isArray(messages.dmChats?.[charId]) ? messages.dmChats[charId] : [])
      .filter(message => !message.deleted)
      .slice()
      .sort((a, b) => messageTimestamp(b) - messageTimestamp(a))
      .slice(0, dmLimit);
  } else if (path === 'messagePanelHistory') {
    Object.entries(messages.dmChats || {}).forEach(([dmCharId, entries]) => {
      dmChats[dmCharId] = (Array.isArray(entries) ? entries : [])
        .filter(message => !message.deleted)
        .slice()
        .sort((a, b) => messageTimestamp(b) - messageTimestamp(a))
        .slice(0, dmLimit);
    });
  }
  return {
    ...messages,
    statusUpdates,
    dmChats,
  };
};

const auditTimestamp = (entry) => {
  if (typeof entry?.ts === 'number') return entry.ts;
  if (typeof entry?.createdAt === 'number') return entry.createdAt;
  const parsed = Date.parse(entry?.ts || entry?.createdAt || '');
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeAuditState = (audit = {}) => ({
  ...(audit && typeof audit === 'object' ? audit : {}),
  activeSessionId: audit?.activeSessionId || null,
  sessions: audit?.sessions && typeof audit.sessions === 'object' ? audit.sessions : {},
  events: audit?.events && typeof audit.events === 'object' ? audit.events : {},
  snapshots: audit?.snapshots && typeof audit.snapshots === 'object' ? audit.snapshots : {},
});

const deriveAuditVirtualValue = (state, path, args = {}) => {
  const audit = normalizeAuditState(state.audit);
  if (path === 'auditSessionMeta') {
    return {
      activeSessionId: audit.activeSessionId,
      sessions: audit.sessions,
    };
  }
  if (path === 'auditMeta') {
    return {
      activeSessionId: audit.activeSessionId,
      sessions: audit.sessions,
      snapshots: audit.snapshots,
    };
  }
  if (path === 'auditRecent') {
    const limit = asPositiveInteger(args.limit, 100, 500);
    const events = Object.values(audit.events || {})
      .filter(Boolean)
      .slice()
      .sort((a, b) => auditTimestamp(b) - auditTimestamp(a))
      .slice(0, limit);
    return {
      events: Object.fromEntries(events.map((event) => [event.id, event])),
    };
  }
  return undefined;
};

const normalizeEntryCommandPayload = (args = {}, label = 'entry') => {
  const entry = args.entry && typeof args.entry === 'object' ? args.entry : args;
  const entryId = typeof args.entryId === 'string' && args.entryId.trim()
    ? args.entryId.trim()
    : (typeof entry?.id === 'string' ? entry.id.trim() : '');
  if (!entryId) throw new Error(`${label} id is required`);
  return {
    entryId,
    entry: { ...(entry || {}), id: entryId },
  };
};

const normalizeEntryDeletePayload = (args = {}, label = 'entry') => {
  const entryId = typeof args.entryId === 'string' && args.entryId.trim()
    ? args.entryId.trim()
    : (typeof args.id === 'string' ? args.id.trim() : '');
  if (!entryId) throw new Error(`${label} id is required`);
  return { entryId };
};

const normalizeNameKey = (value) => (
  typeof value === 'string' ? value.trim().toLowerCase() : ''
);

const cloneValue = (value) => {
  if (Array.isArray(value)) return value.map(cloneValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, innerValue]) => [key, cloneValue(innerValue)]),
    );
  }
  return value;
};

// Authority-field stripping and sandbox char-row matching live in
// campaignSandboxContract.js (shared with seed builders + sandbox storage).
// This driver used to keep its own field list — forked lists drift, and a
// field present in one list but not the other is a silent leak.

const parseOptionalJsonPayload = (value, fallback = {}) => {
  if (value && typeof value === 'object') return value;
  if (typeof value !== 'string' || !value.trim()) return fallback;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : fallback;
  } catch (_) {
    return fallback;
  }
};

const hasExpectedFields = (source = {}, expected = null) => {
  if (!expected || typeof expected !== 'object') return true;
  return Object.entries(expected).every(([key, expectedValue]) => (
    JSON.stringify(source?.[key]) === JSON.stringify(expectedValue)
  ));
};

const pickDefinedPatchFields = (patch = {}, allowedFields = []) => {
  if (!patch || typeof patch !== 'object') return {};
  return Object.fromEntries(
    allowedFields
      .filter((field) => patch[field] !== undefined)
      .map((field) => [field, cloneValue(patch[field])]),
  );
};

const sanitizeAuditActionSegment = (value, fallback = 'action') => (
  String(value || fallback)
    .trim()
    .replace(/[^A-Za-z0-9_.:-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    || fallback
);

const normalizeJournalNameLookups = (args = {}) => {
  const sourceLookups = Array.isArray(args.lookups) && args.lookups.length > 0
    ? args.lookups
    : [{ category: args.category, name: args.name, nameKey: args.nameKey }];
  return sourceLookups
    .map(lookup => ({
      category: typeof lookup?.category === 'string' ? lookup.category.trim() : '',
      nameKey: normalizeNameKey(lookup?.nameKey || lookup?.name || ''),
    }))
    .filter(lookup => lookup.category && lookup.nameKey)
    .slice(0, 50);
};

const lookupLocalJournalEntriesByName = (entries, args = {}) => {
  const lookups = normalizeJournalNameLookups(args);
  const limit = asPositiveInteger(args.limit, 10, 25);
  return {
    scopeKey: 'global',
    lookups: lookups.map(lookup => {
      const matches = normalizeJournalEntries(entries)
        .filter(entry => (
          entry?.category === lookup.category
          && normalizeNameKey(entry?.name || '') === lookup.nameKey
        ))
        .slice(0, limit)
        .map(entry => ({ entry }));
      return {
        ...lookup,
        rowCount: matches.length,
        visibleCount: matches.length,
        fallback: false,
        entries: matches,
      };
    }),
  };
};

const DEFAULT_DISCOVERY_CATEGORIES = ['quests', 'creatures', 'npcs', 'items', 'recipes', 'diseases', 'plants', 'locations'];

const normalizeDiscoveryList = (value, fallback = [], maxItems = 20, maxLength = 120) => {
  const source = Array.isArray(value) ? value : fallback;
  const seen = new Set();
  const result = [];
  source.forEach(item => {
    const normalized = typeof item === 'string' ? item.trim().slice(0, maxLength) : '';
    const key = normalizeNameKey(normalized);
    if (!key || seen.has(key)) return;
    seen.add(key);
    result.push(normalized);
  });
  return result.slice(0, maxItems);
};

const scoreDiscoveryName = (query, value) => {
  const q = normalizeNameKey(query);
  const n = normalizeNameKey(value);
  if (!q || !n) return 0;
  if (n === q) return 300;
  if (q.length < 3) return 0;
  if (n.startsWith(q)) return 220;
  const tokens = n.split(/\s+/).filter(Boolean);
  if (tokens.some(token => token === q)) return 200;
  if (tokens.some(token => token.startsWith(q))) return 180;
  if (n.includes(q)) return 120;
  return 0;
};

const sessionNumber = (entry) => {
  if (Number.isFinite(entry?.sessionNumber)) return Math.trunc(entry.sessionNumber);
  const match = typeof entry?.name === 'string' ? entry.name.match(/\bSession\s+(\d+)\b/i) : null;
  if (!match) return null;
  const parsed = Number.parseInt(match[1], 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const buildSessionMeta = (entries) => {
  const sessions = normalizeJournalEntries(entries)
    .filter(entry => entry.category === 'session');
  const numbers = sessions.map(sessionNumber).filter(Number.isFinite);
  return {
    count: sessions.length,
    maxSessionNumber: numbers.length > 0 ? Math.max(...numbers) : sessions.length,
    latestSession: sessions[0] || null,
  };
};

const questMatchesNpc = (quest, npcName) => {
  const key = normalizeNameKey(npcName);
  if (!key) return false;
  const description = normalizeNameKey(quest?.description || quest?.summary || '');
  const questGiver = normalizeNameKey(quest?.questGiver || '');
  const relatedNpcs = Array.isArray(quest?.relatedNpcs)
    ? quest.relatedNpcs.map(normalizeNameKey)
    : [];
  return description.includes(key) || questGiver === key || relatedNpcs.includes(key);
};

const discoverLocalJournalEntries = (entries, args = {}) => {
  const query = typeof args.query === 'string' ? args.query.trim().slice(0, 120) : '';
  const explicitCategories = Array.isArray(args.categories) && args.categories.length > 0;
  const categories = normalizeDiscoveryList(args.categories, DEFAULT_DISCOVERY_CATEGORIES, 12, 48)
    .filter(category => category !== 'session');
  const npcRefs = normalizeDiscoveryList(args.npcRefs, [], 20, 120);
  const questNpcNames = normalizeDiscoveryList(args.questNpcNames, [], 20, 120);
  const limit = asPositiveInteger(args.limit, 10, 50);
  const candidates = normalizeJournalEntries(entries);
  const titleCandidates = query || explicitCategories
    ? candidates.filter(entry => categories.includes(entry.category) && entry.name)
    : [];
  const entriesResult = titleCandidates
    .map(entry => ({
      entry,
      score: query ? Math.max(scoreDiscoveryName(query, entry.name), scoreDiscoveryName(query, entry.title)) : 1,
    }))
    .filter(item => item.score > 0)
    .sort((a, b) => b.score - a.score || entryTimestamp(b.entry) - entryTimestamp(a.entry))
    .slice(0, limit)
    .map(item => item.entry);
  const npcEntries = candidates.filter(entry => entry.category === 'npcs' && entry.name);
  const questEntries = candidates.filter(entry => (
    entry.category === 'quests'
    && !['completed', 'complete', 'done', 'closed'].includes(normalizeNameKey(entry.status || ''))
  ));
  return {
    scopeKey: 'global',
    query,
    categories,
    limit,
    categoryLimit: asPositiveInteger(args.categoryLimit, 50, 100),
    entries: entriesResult,
    npcMatches: npcRefs.map(refName => ({
      refName,
      entries: npcEntries
        .map(entry => ({ entry, score: scoreDiscoveryName(refName, entry.name) }))
        .filter(item => item.score > 0)
        .sort((a, b) => b.score - a.score || entryTimestamp(b.entry) - entryTimestamp(a.entry))
        .slice(0, Math.min(5, limit))
        .map(item => item.entry),
    })),
    questMatches: questNpcNames.map(npcName => ({
      npcName,
      entries: questEntries
        .filter(entry => questMatchesNpc(entry, npcName))
        .slice(0, Math.min(10, limit)),
    })),
    sessionMeta: args.includeSessionMeta ? buildSessionMeta(candidates) : null,
  };
};

/**
 * @param {{ defaults: Object, demoData?: Object, useServerDerivedViewerQueries?: boolean }} config
 * @returns {import('./StorageDriver.js').StorageDriver}
 */
export const createLocalDriver = (config = { defaults: {}, demoData: {} }) => {
  const sandboxPersistenceEnabled = isCampaignSandboxStorageConfig(config);
  const sandboxId = getCampaignSandboxIdFromConfig(config);
  let state = loadCampaignSandboxState(config, buildInitialData(config));
  let lastSandboxPersistenceOk = true;
  /** @type {Map<string, Set<(value: any) => void>>} */
  const handlers = new Map();
  const subscriptionArgs = new Map();

  // Sandbox persistence is SYNCHRONOUS by contract: every mutation is fully
  // written to localStorage before the operation returns, so a tab close at
  // any moment loses nothing and the `persisted` status each op reports is
  // about THIS write. (Microtask coalescing was evaluated 2026-07 and
  // rejected — it breaks the write-before-return guarantee the driver tests
  // encode. If whole-state serialization ever becomes a measured hot spot,
  // the fix is per-collection snapshot keys, not deferred writes.)
  const persistSandboxState = () => {
    if (!sandboxPersistenceEnabled) return true;
    lastSandboxPersistenceOk = saveCampaignSandboxState(config, state);
    return lastSandboxPersistenceOk;
  };

  const withPersistenceStatus = (result) => {
    if (!sandboxPersistenceEnabled || !result || typeof result !== 'object') return result;
    return {
      ...result,
      persisted: lastSandboxPersistenceOk,
      ...(lastSandboxPersistenceOk ? {} : { persistenceError: 'sandbox_persistence_failed' }),
    };
  };

  const getHandlers = (path) => {
    let set = handlers.get(path);
    if (!set) {
      set = new Set();
      handlers.set(path, set);
    }
    return set;
  };

  const notify = (path) => {
    const hs = handlers.get(path);
    if (!hs || hs.size === 0) return;
    const value = readPath(path);
    hs.forEach(h => {
      try { h(value); } catch (err) { console.error(`LocalDriver handler error on ${path}:`, err); }
    });
  };

  const notifyJournalVirtuals = (basePath) => {
    Object.entries(JOURNAL_VIRTUAL_BASE_PATH).forEach(([virtualPath, sourcePath]) => {
      if (sourcePath === basePath) notify(virtualPath);
    });
  };

  const notifyMessageVirtuals = (basePath) => {
    Object.entries(MESSAGE_VIRTUAL_BASE_PATH).forEach(([virtualPath, sourcePath]) => {
      if (sourcePath === basePath) notify(virtualPath);
    });
  };

  const notifyAuditVirtuals = (basePath) => {
    Object.entries(AUDIT_VIRTUAL_BASE_PATH).forEach(([virtualPath, sourcePath]) => {
      if (sourcePath === basePath) notify(virtualPath);
    });
  };

  const readPath = (path) => {
    if (Object.prototype.hasOwnProperty.call(JOURNAL_VIRTUAL_BASE_PATH, path)) {
      return deriveJournalVirtualValue(state, path, subscriptionArgs.get(path) || {});
    }
    if (Object.prototype.hasOwnProperty.call(MESSAGE_VIRTUAL_BASE_PATH, path)) {
      return deriveMessageVirtualValue(state, path, subscriptionArgs.get(path) || {});
    }
    if (Object.prototype.hasOwnProperty.call(AUDIT_VIRTUAL_BASE_PATH, path)) {
      return deriveAuditVirtualValue(state, path, subscriptionArgs.get(path) || {});
    }
    return state[path];
  };

  const isSandboxAuthorityChar = (char, fallbackId = '') => {
    if (!sandboxPersistenceEnabled) return false;
    return isSandboxCharacterForSandbox(char, sandboxId, fallbackId);
  };

  const normalizeSandboxChars = (value = {}) => {
    if (!sandboxPersistenceEnabled || !value || typeof value !== 'object') return value || {};
    return normalizeSandboxStateChars(
      value,
      sandboxId,
      state.globalSettings?.campaignId
        || config?.defaults?.globalSettings?.campaignId
        || null,
    );
  };

  const requireSandboxAuthorityChar = (charId) => {
    if (!sandboxPersistenceEnabled) return null;
    const char = state.chars?.[charId];
    if (!isSandboxAuthorityChar(char)) return null;
    return char;
  };

  const sandboxCharIds = () => new Set(
    Object.values(state.chars || {})
      .filter((char) => isSandboxAuthorityChar(char))
      .map(char => String(char.id || char.charId || ''))
      .filter(Boolean)
  );

  const withSandboxDomainMetadata = (payload = {}, domain = 'sandbox') => {
    if (!sandboxPersistenceEnabled || !payload || typeof payload !== 'object') return payload;
    return {
      ...payload,
      scope: payload.scope || 'sandbox',
      sandboxId: payload.sandboxId || sandboxId,
      runtimeScope: payload.runtimeScope || 'campaignSandbox',
      sandboxMetadata: {
        ...(payload.sandboxMetadata && typeof payload.sandboxMetadata === 'object' ? payload.sandboxMetadata : {}),
        domain,
        sandboxId,
        scope: 'sandbox',
      },
    };
  };

  const withSandboxDomainArray = (value, domain) => (
    sandboxPersistenceEnabled && Array.isArray(value)
      ? value.map(entry => (entry && typeof entry === 'object' ? withSandboxDomainMetadata(entry, domain) : entry))
      : value
  );

  const withSandboxNpcLibraryMetadata = (value) => {
    if (!sandboxPersistenceEnabled || !value || typeof value !== 'object' || Array.isArray(value)) return value;
    return Object.fromEntries(
      Object.entries(value).map(([npcId, npc]) => [
        npcId,
        npc && typeof npc === 'object' ? withSandboxDomainMetadata(npc, 'npcLibrary') : npc,
      ])
    );
  };

  const normalizeSandboxMessage = (message = {}) => {
    if (!sandboxPersistenceEnabled || !message || typeof message !== 'object') return message;
    const allowed = sandboxCharIds();
    const recipients = Array.isArray(message.recipients)
      ? message.recipients.map(String).filter(charId => allowed.has(charId))
      : [];
    if (message.visibility === 'private' && recipients.length === 0) return null;
    return withSandboxDomainMetadata({
      ...message,
      visibility: message.visibility || 'public',
      recipients: message.visibility === 'public' ? recipients : recipients,
    }, 'messages');
  };

  const normalizeSandboxMessages = (value = {}) => {
    const messages = normalizeMessagesState(value);
    if (!sandboxPersistenceEnabled) return messages;
    const allowed = sandboxCharIds();
    const statusUpdates = (messages.statusUpdates || [])
      .map(normalizeSandboxMessage)
      .filter(Boolean);
    const dmChats = {};
    Object.entries(messages.dmChats || {}).forEach(([charId, entries]) => {
      if (!allowed.has(String(charId))) return;
      dmChats[charId] = (Array.isArray(entries) ? entries : [])
        .map(message => withSandboxDomainMetadata(message, 'dmChats'));
    });
    return {
      ...messages,
      statusUpdates,
      dmChats,
    };
  };

  const normalizeSandboxPathValue = (path, value) => {
    if (!sandboxPersistenceEnabled) return value;
    if (path === 'chars') return normalizeSandboxChars(value);
    if (path === 'journal') return withSandboxDomainArray(value, 'journal');
    if (path === 'dmJournal') return withSandboxDomainArray(value, 'dmJournal');
    if (path === 'npcLibrary') return withSandboxNpcLibraryMetadata(value);
    if (path === 'groupTravelLog') return withSandboxDomainArray(value, 'travelLog');
    if (path === 'messages') return normalizeSandboxMessages(value);
    return value;
  };

  if (sandboxPersistenceEnabled) {
    state = {
      ...state,
      chars: normalizeSandboxChars(state.chars),
      messages: normalizeSandboxMessages(state.messages),
    };
  }
  persistSandboxState();

  const appendLocalAuthorityAudit = ({
    category = 'sandbox',
    action = 'sandbox.local-authority',
    summary = 'Sandbox local authority action',
    target = null,
    details = {},
    audit = null,
  } = {}) => {
    if (!sandboxPersistenceEnabled) return null;
    const auditPayload = audit && typeof audit === 'object' ? audit : {};
    const eventId = auditPayload.id || generateLocalId();
    const actionSegment = sanitizeAuditActionSegment(auditPayload.action || action);
    const payload = {
      ...auditPayload,
      id: eventId,
      ts: auditPayload.ts || Date.now(),
      category: auditPayload.category || category,
      action: auditPayload.action || actionSegment,
      summary: auditPayload.summary || summary,
      target: auditPayload.target || target || undefined,
      details: {
        ...(details || {}),
        ...(auditPayload.details && typeof auditPayload.details === 'object' ? auditPayload.details : {}),
        scope: 'sandbox',
        sandboxId,
      },
      scope: 'sandbox',
      sandboxId,
      localAuthority: true,
      authoritative: false,
    };
    state = applyPatchLocal(state, { [`audit/events/${eventId}`]: payload });
    return eventId;
  };

  const withSandboxAuditMetadata = (payload = {}) => {
    if (!sandboxPersistenceEnabled || !payload || typeof payload !== 'object') return payload;
    return {
      ...payload,
      details: {
        ...(payload.details && typeof payload.details === 'object' ? payload.details : {}),
        scope: payload.details?.scope || 'sandbox',
        sandboxId: payload.details?.sandboxId || sandboxId,
      },
      scope: payload.scope || 'sandbox',
      sandboxId: payload.sandboxId || sandboxId,
      authoritative: payload.authoritative === true,
    };
  };

  const withSandboxSessionMetadata = (payload = {}) => {
    if (!sandboxPersistenceEnabled || !payload || typeof payload !== 'object') return payload;
    return {
      ...payload,
      scope: payload.scope || 'sandbox',
      sandboxId: payload.sandboxId || sandboxId,
      authoritative: payload.authoritative === true,
    };
  };

  const commitLocalAuthorityState = ({
    nextChars = null,
    nextPartyInventory = null,
    auditEvent = null,
  } = {}) => {
    state = {
      ...state,
      ...(nextChars ? { chars: nextChars } : {}),
      ...(nextPartyInventory ? { partyInventory: nextPartyInventory } : {}),
    };
    if (auditEvent) appendLocalAuthorityAudit(auditEvent);
    persistSandboxState();
    if (nextChars) notify('chars');
    if (nextPartyInventory) notify('partyInventory');
    if (auditEvent) notify('audit');
  };

  const commitLocalCharAuthority = (charId, nextCharData, auditEvent = null) => {
    if (!nextCharData) return null;
    const nextChars = {
      ...(state.chars || {}),
      [charId]: nextCharData,
    };
    commitLocalAuthorityState({ nextChars, auditEvent });
    return nextCharData;
  };

  const localEntryIdFactory = (prefix = 'sandbox-entry') => () => generateLocalId().replace(/^local-/, `${prefix}-`);
  const localAbilityIdFactory = (entryPrefix = 'sandbox-entry') => (abilityId = 'ability') => (
    `${localEntryIdFactory(entryPrefix)()}:ability:${sanitizeAuditActionSegment(abilityId, 'ability')}`
  );

  const applySystemStatePatch = (charId, {
    updates = null,
    expected = null,
    audit = null,
    category = 'systems',
    action = 'sandbox.system.patch',
  } = {}) => {
    const char = requireSandboxAuthorityChar(charId);
    if (!char) return null;
    const safeUpdates = pickDefinedPatchFields(updates, ['systemList', 'removedSystems', 'glowingSystems']);
    if (Object.keys(safeUpdates).length === 0) return null;
    if (!hasExpectedFields(char, expected)) {
      throw new Error('local sandbox authority conflict: character system state changed');
    }
    const nextCharData = { ...char, ...safeUpdates };
    commitLocalCharAuthority(charId, nextCharData, {
      category,
      action,
      summary: audit?.summary || `Sandbox system state updated for ${char.name || charId}`,
      target: { charId, charName: char.name || charId },
      details: { fields: Object.keys(safeUpdates) },
      audit,
    });
    return withPersistenceStatus({ ok: true, charId, fields: Object.keys(safeUpdates), localAuthority: true, scope: 'sandbox', sandboxId });
  };

  const applyCombinedSystemPatch = (charId, {
    systemUpdates = null,
    systemExpected = null,
    secondaryUpdates = null,
    secondaryExpected = null,
    secondaryFields = [],
    audit = null,
    action = 'sandbox.system.combined-patch',
  } = {}) => {
    const char = requireSandboxAuthorityChar(charId);
    if (!char) return null;
    const safeSystemUpdates = pickDefinedPatchFields(systemUpdates, ['systemList', 'removedSystems', 'glowingSystems']);
    const safeSecondaryUpdates = pickDefinedPatchFields(secondaryUpdates, secondaryFields);
    const fields = [...Object.keys(safeSystemUpdates), ...Object.keys(safeSecondaryUpdates)];
    if (fields.length === 0) return null;
    if (!hasExpectedFields(char, systemExpected) || !hasExpectedFields(char, secondaryExpected)) {
      throw new Error('local sandbox authority conflict: character state changed');
    }
    const nextCharData = { ...char, ...safeSystemUpdates, ...safeSecondaryUpdates };
    commitLocalCharAuthority(charId, nextCharData, {
      category: 'systems',
      action,
      summary: audit?.summary || `Sandbox character state updated for ${char.name || charId}`,
      target: { charId, charName: char.name || charId },
      details: { fields },
      audit,
    });
    return withPersistenceStatus({ ok: true, charId, fields, localAuthority: true, scope: 'sandbox', sandboxId });
  };

  const applyCharItemAuthority = (charId, applyFn, args = {}, auditEvent = {}) => {
    const char = requireSandboxAuthorityChar(charId);
    if (!char) return null;
    const result = applyFn(char, args);
    if (!result?.nextCharData) return null;
    commitLocalCharAuthority(charId, result.nextCharData, {
      category: 'inventory',
      target: { charId, charName: char.name || charId },
      ...auditEvent,
    });
    return result;
  };

  return {
    kind: 'local',

    setSubscriptionArgs(path, args) {
      subscriptionArgs.set(path, args && typeof args === 'object' ? args : {});
      notify(path);
    },

    subscribe(path, handler) {
      const set = getHandlers(path);
      set.add(handler);
      // Deliver current value synchronously so consumers don't race the first render.
      try { handler(readPath(path)); } catch (err) { console.error(`LocalDriver initial emit error on ${path}:`, err); }
      return () => { set.delete(handler); };
    },

    save(path, value) {
      state = { ...state, [path]: normalizeSandboxPathValue(path, value) };
      persistSandboxState();
      notify(path);
      notifyJournalVirtuals(path);
      notifyMessageVirtuals(path);
      notifyAuditVirtuals(path);
    },

    async upsertJournalEntry(args = {}) {
      const { entryId, entry } = normalizeEntryCommandPayload(args, 'journal entry');
      const current = Array.isArray(state.journal) ? state.journal : [];
      const nextEntry = withSandboxDomainMetadata(entry, 'journal');
      const replaced = current.some(item => item?.id === entryId);
      state = {
        ...state,
        journal: replaced
          ? current.map(item => item?.id === entryId ? nextEntry : item)
          : [...current, nextEntry],
      };
      persistSandboxState();
      notify('journal');
      notifyJournalVirtuals('journal');
      return withPersistenceStatus({ ok: true, scopeKey: sandboxPersistenceEnabled ? 'sandbox' : 'global', sandboxId: sandboxPersistenceEnabled ? sandboxId : undefined, entryId });
    },

    async deleteJournalEntry(args = {}) {
      const { entryId } = normalizeEntryDeletePayload(args, 'journal entry');
      const current = Array.isArray(state.journal) ? state.journal : [];
      const next = current.filter(item => item?.id !== entryId);
      state = { ...state, journal: next };
      persistSandboxState();
      notify('journal');
      notifyJournalVirtuals('journal');
      return withPersistenceStatus({ ok: true, scopeKey: sandboxPersistenceEnabled ? 'sandbox' : 'global', sandboxId: sandboxPersistenceEnabled ? sandboxId : undefined, entryId, deleted: next.length !== current.length });
    },

    // Campaign tag (2026-07-13): the local journal is a single flat array, so
    // the "move" is just stamping/clearing campaignId on the entry in place.
    async setJournalEntryCampaignScope(args = {}) {
      const entryId = typeof args.entryId === 'string' ? args.entryId.trim() : '';
      if (!entryId) throw new Error('setJournalEntryCampaignScope requires an entryId');
      const campaignId = args.campaignId ? String(args.campaignId) : null;
      const current = Array.isArray(state.journal) ? state.journal : [];
      let found = false;
      const next = current.map(item => {
        if (item?.id !== entryId) return item;
        found = true;
        const nextEntry = { ...item, updatedAt: new Date().toISOString() };
        if (campaignId) nextEntry.campaignId = campaignId;
        else delete nextEntry.campaignId;
        return nextEntry;
      });
      if (!found) throw new Error('journal entry not found');
      state = { ...state, journal: next };
      persistSandboxState();
      notify('journal');
      notifyJournalVirtuals('journal');
      return withPersistenceStatus({ ok: true, entryId, campaignId, scopeKey: campaignId ? `campaign:${campaignId}` : 'global' });
    },

    async upsertDmJournalEntry(args = {}) {
      const { entryId, entry } = normalizeEntryCommandPayload(args, 'DM journal entry');
      const current = Array.isArray(state.dmJournal) ? state.dmJournal : [];
      const nextEntry = withSandboxDomainMetadata(entry, 'dmJournal');
      const replaced = current.some(item => item?.id === entryId);
      state = {
        ...state,
        dmJournal: replaced
          ? current.map(item => item?.id === entryId ? nextEntry : item)
          : [...current, nextEntry],
      };
      persistSandboxState();
      notify('dmJournal');
      notifyJournalVirtuals('dmJournal');
      return withPersistenceStatus({ ok: true, scopeKey: sandboxPersistenceEnabled ? 'sandbox' : 'global', sandboxId: sandboxPersistenceEnabled ? sandboxId : undefined, entryId });
    },

    async deleteDmJournalEntry(args = {}) {
      const { entryId } = normalizeEntryDeletePayload(args, 'DM journal entry');
      const current = Array.isArray(state.dmJournal) ? state.dmJournal : [];
      const next = current.filter(item => item?.id !== entryId);
      state = { ...state, dmJournal: next };
      persistSandboxState();
      notify('dmJournal');
      notifyJournalVirtuals('dmJournal');
      return withPersistenceStatus({ ok: true, scopeKey: sandboxPersistenceEnabled ? 'sandbox' : 'global', sandboxId: sandboxPersistenceEnabled ? sandboxId : undefined, entryId, deleted: next.length !== current.length });
    },

    async upsertGlobalLoreEntry(args = {}) {
      return await this.upsertJournalEntry(args);
    },

    async deleteGlobalLoreEntry(args = {}) {
      return await this.deleteJournalEntry(args);
    },

    async lookupJournalEntriesByName(args = {}) {
      return { ...lookupLocalJournalEntriesByName(state.journal, args), scopeKey: sandboxPersistenceEnabled ? 'sandbox' : 'global' };
    },

    async lookupDmJournalEntriesByName(args = {}) {
      return { ...lookupLocalJournalEntriesByName(state.dmJournal, args), scopeKey: sandboxPersistenceEnabled ? 'sandbox' : 'global' };
    },

    async discoverJournalEntries(args = {}) {
      return { ...discoverLocalJournalEntries(state.journal, args), scopeKey: sandboxPersistenceEnabled ? 'sandbox' : 'global' };
    },

    async discoverDmJournalEntries(args = {}) {
      return { ...discoverLocalJournalEntries(state.dmJournal, args), scopeKey: sandboxPersistenceEnabled ? 'sandbox' : 'global' };
    },

    async sendStatusMessage(args = {}) {
      const id = args.message?.id || generateLocalId();
      const message = normalizeSandboxMessage({ ...(args.message || {}), id });
      if (!message) return null;
      state = {
        ...state,
        messages: {
          ...normalizeMessagesState(state.messages),
          statusUpdates: [message, ...(normalizeMessagesState(state.messages).statusUpdates || [])],
        },
      };
      persistSandboxState();
      notify('messages');
      notifyMessageVirtuals('messages');
      return withPersistenceStatus({ ok: true, messageId: id, scopeKey: sandboxPersistenceEnabled ? 'sandbox' : 'global', sandboxId: sandboxPersistenceEnabled ? sandboxId : undefined });
    },

    async markStatusMessageSeen(args = {}) {
      const messageId = typeof args.messageId === 'string' ? args.messageId : '';
      if (!messageId) return null;
      const messages = normalizeMessagesState(state.messages);
      state = {
        ...state,
        messages: {
          ...messages,
          statusUpdates: messages.statusUpdates.map(message => (
            message?.id === messageId ? { ...message, seen: true } : message
          )),
        },
      };
      persistSandboxState();
      notify('messages');
      notifyMessageVirtuals('messages');
      return withPersistenceStatus({ ok: true, messageId, scopeKey: sandboxPersistenceEnabled ? 'sandbox' : 'global', sandboxId: sandboxPersistenceEnabled ? sandboxId : undefined });
    },

    async dismissStatusMessage(args = {}) {
      const messageId = typeof args.messageId === 'string' ? args.messageId : '';
      if (!messageId) return null;
      const messages = normalizeMessagesState(state.messages);
      state = {
        ...state,
        messages: {
          ...messages,
          statusUpdates: messages.statusUpdates.map(message => (
            message?.id === messageId ? { ...message, dismissed: true } : message
          )),
        },
      };
      persistSandboxState();
      notify('messages');
      notifyMessageVirtuals('messages');
      return withPersistenceStatus({ ok: true, messageId, scopeKey: sandboxPersistenceEnabled ? 'sandbox' : 'global', sandboxId: sandboxPersistenceEnabled ? sandboxId : undefined });
    },

    async sendDmChatMessage(args = {}) {
      const charId = typeof args.charId === 'string' ? args.charId : '';
      if (sandboxPersistenceEnabled && !requireSandboxAuthorityChar(charId)) return null;
      const id = args.message?.id || generateLocalId();
      const message = withSandboxDomainMetadata({ ...(args.message || {}), id }, 'dmChats');
      const messages = normalizeMessagesState(state.messages);
      state = {
        ...state,
        messages: {
          ...messages,
          dmChats: {
            ...(messages.dmChats || {}),
            [charId]: [message, ...((messages.dmChats || {})[charId] || [])],
          },
        },
      };
      persistSandboxState();
      notify('messages');
      notifyMessageVirtuals('messages');
      return withPersistenceStatus({ ok: true, charId, messageId: id, scopeKey: sandboxPersistenceEnabled ? 'sandbox' : 'global', sandboxId: sandboxPersistenceEnabled ? sandboxId : undefined });
    },

    async markDmChatRead(args = {}) {
      const charId = typeof args.charId === 'string' ? args.charId : '';
      if (sandboxPersistenceEnabled && !requireSandboxAuthorityChar(charId)) return null;
      const ids = new Set(Array.isArray(args.messageIds) ? args.messageIds : []);
      if (!charId || ids.size === 0) return null;
      const messages = normalizeMessagesState(state.messages);
      state = {
        ...state,
        messages: {
          ...messages,
          dmChats: {
            ...(messages.dmChats || {}),
            [charId]: ((messages.dmChats || {})[charId] || []).map(message => (
              ids.has(message?.id) ? { ...message, read: true } : message
            )),
          },
        },
      };
      persistSandboxState();
      notify('messages');
      notifyMessageVirtuals('messages');
      return withPersistenceStatus({ ok: true, charId, messageIds: [...ids], scopeKey: sandboxPersistenceEnabled ? 'sandbox' : 'global', sandboxId: sandboxPersistenceEnabled ? sandboxId : undefined });
    },

    async deleteDmChatMessage(args = {}) {
      const charId = typeof args.charId === 'string' ? args.charId : '';
      const messageId = typeof args.messageId === 'string' ? args.messageId : '';
      if (sandboxPersistenceEnabled && !requireSandboxAuthorityChar(charId)) return null;
      if (!charId || !messageId) return null;
      const messages = normalizeMessagesState(state.messages);
      state = {
        ...state,
        messages: {
          ...messages,
          dmChats: {
            ...(messages.dmChats || {}),
            [charId]: ((messages.dmChats || {})[charId] || []).map(message => (
              message?.id === messageId ? { ...message, deleted: true } : message
            )),
          },
        },
      };
      persistSandboxState();
      notify('messages');
      notifyMessageVirtuals('messages');
      return withPersistenceStatus({ ok: true, charId, messageId, scopeKey: sandboxPersistenceEnabled ? 'sandbox' : 'global', sandboxId: sandboxPersistenceEnabled ? sandboxId : undefined });
    },

    async patchCharacterVitals(args = {}) {
      const char = requireSandboxAuthorityChar(args.charId);
      if (!char) return null;
      const updates = pickDefinedPatchFields(args.updates, ['currentHp', 'tempHp', 'currentMana']);
      if (Object.keys(updates).length === 0) return null;
      if (!hasExpectedFields(char, args.expected)) {
        throw new Error('local sandbox authority conflict: character vitals changed');
      }
      commitLocalCharAuthority(args.charId, { ...char, ...updates }, {
        category: 'characters',
        action: 'sandbox.character.vitals.patch',
        summary: `Sandbox vitals updated for ${char.name || args.charId}`,
        target: { charId: args.charId, charName: char.name || args.charId },
        details: { fields: Object.keys(updates), surface: args.surface || null },
      });
      return withPersistenceStatus({
        ok: true,
        charId: args.charId,
        fields: Object.keys(updates),
        localAuthority: true,
        scope: 'sandbox',
        sandboxId,
      });
    },

    async patchCharacterSystemInstance(args = {}) {
      const char = requireSandboxAuthorityChar(args.charId);
      if (!char || !args.instanceKey || !args.patch || typeof args.patch !== 'object') return null;
      const systemList = Array.isArray(char.systemList) ? char.systemList : [];
      let patched = false;
      const nextSystemList = systemList.map((entry) => {
        const entryKey = entry?.id || entry?.systemId;
        if (entryKey !== args.instanceKey) return entry;
        patched = true;
        const nextEntry = { ...entry, ...cloneValue(args.patch) };
        if (args.historyEntry && typeof args.historyEntry === 'object') {
          nextEntry.history = [
            ...(Array.isArray(entry.history) ? entry.history : []),
            cloneValue(args.historyEntry),
          ];
        }
        return nextEntry;
      });
      if (!patched) return null;
      commitLocalCharAuthority(args.charId, { ...char, systemList: nextSystemList }, {
        category: 'systems',
        action: 'sandbox.system.instance.patch',
        summary: `Sandbox system instance updated for ${char.name || args.charId}`,
        target: { charId: args.charId, charName: char.name || args.charId },
        details: { instanceKey: args.instanceKey, fields: Object.keys(args.patch || {}) },
      });
      return withPersistenceStatus({
        ok: true,
        charId: args.charId,
        instanceKey: args.instanceKey,
        localAuthority: true,
        scope: 'sandbox',
        sandboxId,
      });
    },

    async patchCharacterSystemState(args = {}) {
      return applySystemStatePatch(args.charId, {
        updates: args.updates,
        expected: args.expected,
        audit: args.audit,
        action: 'sandbox.system.state.patch',
      });
    },

    async patchCharacterSystemStateAndVitals(args = {}) {
      return applyCombinedSystemPatch(args.charId, {
        systemUpdates: args.systemUpdates,
        systemExpected: args.systemExpected,
        secondaryUpdates: args.vitalsUpdates,
        secondaryExpected: args.vitalsExpected,
        secondaryFields: ['currentHp', 'tempHp', 'currentMana'],
        audit: args.audit,
        action: 'sandbox.system.state-vitals.patch',
      });
    },

    async patchCharacterSystemStateAndResources(args = {}) {
      return applyCombinedSystemPatch(args.charId, {
        systemUpdates: args.systemUpdates,
        systemExpected: args.systemExpected,
        secondaryUpdates: args.resourceUpdates,
        secondaryExpected: args.resourceExpected,
        secondaryFields: ['currentHp', 'tempHp', 'currentMana', 'exhaustionLevel', 'hitDiceRemaining'],
        audit: args.audit,
        action: 'sandbox.system.state-resources.patch',
      });
    },

    async patchCharacterSystemStateAndEffects(args = {}) {
      return applyCombinedSystemPatch(args.charId, {
        systemUpdates: args.systemUpdates,
        systemExpected: args.systemExpected,
        secondaryUpdates: args.effectUpdates,
        secondaryExpected: args.effectExpected,
        secondaryFields: [
          'buffList',
          'debuffList',
          'permanentBuffs',
          'permanentDebuffs',
          'buffSources',
          'concentratingOn',
          'concentrationEffectGroupId',
        ],
        audit: args.audit,
        action: 'sandbox.system.state-effects.patch',
      });
    },

    async depositPartyInventoryFromChar(args = {}) {
      const char = requireSandboxAuthorityChar(args.charId);
      if (!char) return null;
      const result = applyPartyDepositToCharAndPool(char, state.partyInventory || { items: [] }, {
        itemEntryId: args.itemEntryId,
        quantity: args.quantity,
        entryIdFactory: localEntryIdFactory('sandbox-party'),
        abilityIdFactory: localAbilityIdFactory('sandbox-party'),
      });
      if (!result?.nextCharData || !result?.nextPartyInventory) return null;
      commitLocalAuthorityState({
        nextChars: { ...(state.chars || {}), [args.charId]: result.nextCharData },
        nextPartyInventory: result.nextPartyInventory,
        auditEvent: {
          category: 'inventory',
          action: 'sandbox.inventory.deposit-party',
          summary: `Sandbox item moved to party inventory for ${char.name || args.charId}`,
          target: { charId: args.charId, charName: char.name || args.charId },
          details: { itemEntryId: args.itemEntryId, movedQuantity: result.movedQuantity },
        },
      });
      return withPersistenceStatus({
        ok: true,
        charId: args.charId,
        partyItemEntryId: result.partyItemEntryId,
        movedQuantity: result.movedQuantity,
        localAuthority: true,
        scope: 'sandbox',
        sandboxId,
      });
    },

    async takePartyInventoryToChar(args = {}) {
      const char = requireSandboxAuthorityChar(args.charId);
      if (!char) return null;
      const result = applyPartyTakeToCharAndPool(char, state.partyInventory || { items: [] }, {
        itemEntryId: args.itemEntryId,
        quantity: args.quantity,
        entryIdFactory: localEntryIdFactory('sandbox-inventory'),
        abilityIdFactory: localAbilityIdFactory('sandbox-inventory'),
      });
      if (!result?.nextCharData || !result?.nextPartyInventory) return null;
      commitLocalAuthorityState({
        nextChars: { ...(state.chars || {}), [args.charId]: result.nextCharData },
        nextPartyInventory: result.nextPartyInventory,
        auditEvent: {
          category: 'inventory',
          action: 'sandbox.inventory.take-party',
          summary: `Sandbox party item moved to ${char.name || args.charId}`,
          target: { charId: args.charId, charName: char.name || args.charId },
          details: { itemEntryId: args.itemEntryId, movedQuantity: result.movedQuantity },
        },
      });
      return withPersistenceStatus({
        ok: true,
        charId: args.charId,
        itemEntryId: result.targetItemEntryId,
        targetItemEntryId: result.targetItemEntryId,
        movedQuantity: result.movedQuantity,
        localAuthority: true,
        scope: 'sandbox',
        sandboxId,
      });
    },

    async transferInventoryItemBetweenChars(args = {}) {
      const sourceChar = requireSandboxAuthorityChar(args.sourceCharId);
      const targetChar = requireSandboxAuthorityChar(args.targetCharId);
      if (!sourceChar || !targetChar) return null;
      const result = applyCharToCharTransfer(sourceChar, targetChar, {
        itemEntryId: args.itemEntryId,
        quantity: args.quantity,
        entryIdFactory: localEntryIdFactory('sandbox-transfer'),
        abilityIdFactory: localAbilityIdFactory('sandbox-transfer'),
      });
      if (!result?.nextSourceCharData || !result?.nextTargetCharData) return null;
      commitLocalAuthorityState({
        nextChars: {
          ...(state.chars || {}),
          [args.sourceCharId]: result.nextSourceCharData,
          [args.targetCharId]: result.nextTargetCharData,
        },
        auditEvent: {
          category: 'inventory',
          action: 'sandbox.inventory.transfer-character',
          summary: `Sandbox item transferred from ${sourceChar.name || args.sourceCharId} to ${targetChar.name || args.targetCharId}`,
          target: { charId: args.sourceCharId, charName: sourceChar.name || args.sourceCharId },
          details: {
            sourceCharId: args.sourceCharId,
            targetCharId: args.targetCharId,
            itemEntryId: args.itemEntryId,
            movedQuantity: result.movedQuantity,
          },
        },
      });
      return withPersistenceStatus({
        ok: true,
        sourceCharId: args.sourceCharId,
        targetCharId: args.targetCharId,
        destinationItemEntryId: result.targetItemEntryId,
        targetItemEntryId: result.targetItemEntryId,
        movedQuantity: result.movedQuantity,
        localAuthority: true,
        scope: 'sandbox',
        sandboxId,
      });
    },

    async moveCharItemToCraftingTable(args = {}) {
      const result = applyCharItemAuthority(args.charId, applyCharInventoryToCraftingTableTransfer, {
        itemEntryId: args.itemEntryId,
        quantity: args.quantity,
      }, {
        action: 'sandbox.inventory.move-to-crafting-table',
        summary: 'Sandbox item moved to crafting table',
        details: { itemEntryId: args.itemEntryId, movedQuantity: args.quantity || null },
      });
      return result ? withPersistenceStatus({
        ok: true,
        charId: args.charId,
        movedQuantity: result.movedQuantity,
        localAuthority: true,
        scope: 'sandbox',
        sandboxId,
      }) : null;
    },

    async addInventoryItem(args = {}) {
      const result = applyCharItemAuthority(args.charId, applyCharInventoryItemAdd, {
        item: args.item,
      }, {
        action: 'sandbox.inventory.add-item',
        summary: 'Sandbox inventory item added',
        details: { itemName: args.item?.name || null },
      });
      return result ? withPersistenceStatus({
        ok: true,
        charId: args.charId,
        itemEntryId: result.item?.id || args.item?.id || null,
        localAuthority: true,
        scope: 'sandbox',
        sandboxId,
      }) : null;
    },

    async updateInventoryItem(args = {}) {
      const result = applyCharItemAuthority(args.charId, applyCharInventoryItemUpdate, {
        itemEntryId: args.itemEntryId,
        updates: args.updates,
      }, {
        action: 'sandbox.inventory.update-item',
        summary: 'Sandbox inventory item updated',
        details: { itemEntryId: args.itemEntryId, fields: Object.keys(args.updates || {}) },
      });
      return result ? withPersistenceStatus({
        ok: true,
        charId: args.charId,
        itemEntryId: args.itemEntryId,
        localAuthority: true,
        scope: 'sandbox',
        sandboxId,
      }) : null;
    },

    async removeInventoryItem(args = {}) {
      const result = applyCharItemAuthority(args.charId, applyCharInventoryItemRemove, {
        itemEntryId: args.itemEntryId,
      }, {
        action: 'sandbox.inventory.remove-item',
        summary: 'Sandbox inventory item removed',
        details: { itemEntryId: args.itemEntryId },
      });
      return result ? withPersistenceStatus({
        ok: true,
        charId: args.charId,
        itemEntryId: args.itemEntryId,
        localAuthority: true,
        scope: 'sandbox',
        sandboxId,
      }) : null;
    },

    async setInventoryItemFavorite(args = {}) {
      const result = applyCharItemAuthority(args.charId, applyCharInventoryItemFavoriteToggle, {
        itemEntryId: args.itemEntryId,
        favorite: args.favorite,
        expectedFavorited: args.expectedFavorited,
      }, {
        action: 'sandbox.inventory.favorite-item',
        summary: 'Sandbox inventory favorite updated',
        details: { itemEntryId: args.itemEntryId, favorite: args.favorite === true },
      });
      return result ? withPersistenceStatus({
        ok: true,
        charId: args.charId,
        itemEntryId: args.itemEntryId,
        favorite: result.favorited === true,
        localAuthority: true,
        scope: 'sandbox',
        sandboxId,
      }) : null;
    },

    async toggleInventoryItemEquipped(args = {}) {
      const result = applyCharItemAuthority(args.charId, applyCharInventoryItemEquipToggle, {
        itemEntryId: args.itemEntryId,
        expectedEquipped: args.expectedEquipped,
      }, {
        action: 'sandbox.inventory.toggle-equipped',
        summary: 'Sandbox inventory equipped state updated',
        details: { itemEntryId: args.itemEntryId, expectedEquipped: args.expectedEquipped },
      });
      return result ? withPersistenceStatus({
        ok: true,
        charId: args.charId,
        itemEntryId: args.itemEntryId,
        equipped: result.newEquipped === true,
        localAuthority: true,
        scope: 'sandbox',
        sandboxId,
      }) : null;
    },

    async consumeInventoryItem(args = {}) {
      const result = applyCharItemAuthority(args.charId, applyCharInventoryItemConsume, {
        itemEntryId: args.itemEntryId,
        expectedQuantity: args.expectedQuantity,
        expectedUsesCurrent: args.expectedUsesCurrent,
      }, {
        action: 'sandbox.inventory.consume-item',
        summary: 'Sandbox inventory item consumed',
        details: { itemEntryId: args.itemEntryId },
      });
      return result ? withPersistenceStatus({
        ok: true,
        charId: args.charId,
        itemEntryId: args.itemEntryId,
        removedFromInventory: result.removedFromInventory === true,
        localAuthority: true,
        scope: 'sandbox',
        sandboxId,
      }) : null;
    },

    async activateInventoryItemAbility(args = {}) {
      const result = applyCharItemAuthority(args.charId, applyCharInventoryItemAbilityActivation, {
        itemEntryId: args.itemEntryId,
        itemDefinitionId: args.itemDefinitionId,
        abilityId: args.abilityId,
        expectedUsesCurrent: args.expectedUsesCurrent,
        expectedUsesMax: args.expectedUsesMax,
      }, {
        action: 'sandbox.inventory.activate-ability',
        summary: 'Sandbox inventory ability activated',
        details: { itemEntryId: args.itemEntryId, abilityId: args.abilityId },
      });
      return result ? withPersistenceStatus({
        ok: true,
        charId: args.charId,
        itemEntryId: args.itemEntryId,
        abilityId: args.abilityId,
        abilityUsesAfter: result.abilityUsesAfter,
        localAuthority: true,
        scope: 'sandbox',
        sandboxId,
      }) : null;
    },

    async craftOutputToChar(args = {}) {
      const char = requireSandboxAuthorityChar(args.charId);
      if (!char) return null;
      const recipeResult = parseOptionalJsonPayload(args.recipeResult ?? args.recipeResultJson, {});
      const result = applyCraftOutputToCharInventory(char, {
        result: recipeResult,
        quantity: args.quantity,
        consumedIngredients: args.consumedIngredients,
        entryIdFactory: localEntryIdFactory('sandbox-crafted'),
        abilityIdFactory: localAbilityIdFactory('sandbox-crafted'),
      });
      if (!result?.nextCharData) return null;
      commitLocalCharAuthority(args.charId, result.nextCharData, {
        category: 'crafting',
        action: 'sandbox.crafting.output-to-character',
        summary: `Sandbox crafted output added to ${char.name || args.charId}`,
        target: { charId: args.charId, charName: char.name || args.charId },
        details: { itemEntryId: result.itemEntryId, movedQuantity: result.movedQuantity },
      });
      return withPersistenceStatus({
        ok: true,
        charId: args.charId,
        itemEntryId: result.itemEntryId,
        movedQuantity: result.movedQuantity,
        localAuthority: true,
        scope: 'sandbox',
        sandboxId,
      });
    },

    async craftOutputToParty(args = {}) {
      const char = requireSandboxAuthorityChar(args.charId);
      if (!char) return null;
      const recipeResult = parseOptionalJsonPayload(args.recipeResult ?? args.recipeResultJson, {});
      const result = applyCraftOutputToPartyPool(char, state.partyInventory || { items: [] }, {
        result: recipeResult,
        quantity: args.quantity,
        consumedIngredients: args.consumedIngredients,
        craftedEntryIdFactory: localEntryIdFactory('sandbox-crafted'),
        craftedAbilityIdFactory: localAbilityIdFactory('sandbox-crafted'),
        partyEntryIdFactory: localEntryIdFactory('sandbox-party'),
        partyAbilityIdFactory: localAbilityIdFactory('sandbox-party'),
      });
      if (!result?.nextCharData || !result?.nextPartyInventory) return null;
      commitLocalAuthorityState({
        nextChars: { ...(state.chars || {}), [args.charId]: result.nextCharData },
        nextPartyInventory: result.nextPartyInventory,
        auditEvent: {
          category: 'crafting',
          action: 'sandbox.crafting.output-to-party',
          summary: 'Sandbox crafted output added to party inventory',
          target: { charId: args.charId, charName: char.name || args.charId },
          details: { partyItemEntryId: result.partyItemEntryId, movedQuantity: result.movedQuantity },
        },
      });
      return withPersistenceStatus({
        ok: true,
        charId: args.charId,
        partyItemEntryId: result.partyItemEntryId,
        movedQuantity: result.movedQuantity,
        localAuthority: true,
        scope: 'sandbox',
        sandboxId,
      });
    },

    async tableRowToChar(args = {}) {
      const char = requireSandboxAuthorityChar(args.charId);
      if (!char) return null;
      const fallbackResult = parseOptionalJsonPayload(args.fallbackResult ?? args.fallbackResultJson, null);
      const result = applyCraftingTableRowToCharInventory(char, {
        tableItemId: args.tableItemId,
        quantity: args.quantity,
        fallbackResult,
        entryIdFactory: localEntryIdFactory('sandbox-table'),
        abilityIdFactory: localAbilityIdFactory('sandbox-table'),
      });
      if (!result?.nextCharData) return null;
      commitLocalCharAuthority(args.charId, result.nextCharData, {
        category: 'crafting',
        action: 'sandbox.crafting.table-row-to-character',
        summary: `Sandbox crafting table row moved to ${char.name || args.charId}`,
        target: { charId: args.charId, charName: char.name || args.charId },
        details: { tableItemId: args.tableItemId, itemEntryId: result.itemEntryId, movedQuantity: result.movedQuantity },
      });
      return withPersistenceStatus({
        ok: true,
        charId: args.charId,
        itemEntryId: result.itemEntryId,
        movedQuantity: result.movedQuantity,
        localAuthority: true,
        scope: 'sandbox',
        sandboxId,
      });
    },

    async tableRowToParty(args = {}) {
      const char = requireSandboxAuthorityChar(args.charId);
      if (!char) return null;
      const fallbackResult = parseOptionalJsonPayload(args.fallbackResult ?? args.fallbackResultJson, null);
      const result = applyCraftingTableRowToPartyPool(char, state.partyInventory || { items: [] }, {
        tableItemId: args.tableItemId,
        quantity: args.quantity,
        fallbackResult,
        partyEntryIdFactory: localEntryIdFactory('sandbox-party'),
        partyAbilityIdFactory: localAbilityIdFactory('sandbox-party'),
        craftedEntryIdFactory: localEntryIdFactory('sandbox-table-crafted'),
        craftedAbilityIdFactory: localAbilityIdFactory('sandbox-table-crafted'),
      });
      if (!result?.nextCharData || !result?.nextPartyInventory) return null;
      commitLocalAuthorityState({
        nextChars: { ...(state.chars || {}), [args.charId]: result.nextCharData },
        nextPartyInventory: result.nextPartyInventory,
        auditEvent: {
          category: 'crafting',
          action: 'sandbox.crafting.table-row-to-party',
          summary: 'Sandbox crafting table row moved to party inventory',
          target: { charId: args.charId, charName: char.name || args.charId },
          details: { tableItemId: args.tableItemId, partyItemEntryId: result.partyItemEntryId, movedQuantity: result.movedQuantity },
        },
      });
      return withPersistenceStatus({
        ok: true,
        charId: args.charId,
        partyItemEntryId: result.partyItemEntryId,
        movedQuantity: result.movedQuantity,
        localAuthority: true,
        scope: 'sandbox',
        sandboxId,
      });
    },

    async updateMultiple(patch) {
      if (!patch || typeof patch !== 'object') return;
      state = applyPatchLocal(state, patch);
      if (sandboxPersistenceEnabled) {
        state = {
          ...state,
          chars: normalizeSandboxChars(state.chars),
          messages: normalizeSandboxMessages(state.messages),
        };
      }
      persistSandboxState();
      // Determine which top-level collections changed and notify each once.
      const touched = new Set();
      Object.keys(patch).forEach(p => {
        const top = p.split('/')[0];
        if (top) touched.add(top);
      });
      touched.forEach(path => {
        notify(path);
        notifyJournalVirtuals(path);
        notifyMessageVirtuals(path);
        notifyAuditVirtuals(path);
      });
    },

    async pushAuditEvent(event) {
      const id = event?.id || generateLocalId();
      const payload = withSandboxAuditMetadata({ ...(event || {}), id });
      state = applyPatchLocal(state, { [`audit/events/${id}`]: payload });
      persistSandboxState();
      notify('audit');
      notifyAuditVirtuals('audit');
      return id;
    },

    async pushAuditSession(session) {
      const id = session?.id || generateLocalId();
      const payload = withSandboxSessionMetadata({ ...(session || {}), id });
      state = applyPatchLocal(state, { [`audit/sessions/${id}`]: payload });
      persistSandboxState();
      notify('audit');
      notifyAuditVirtuals('audit');
      return id;
    },

    flushPending() {
      // Local writes (including sandbox persistence) are synchronous —
      // nothing to flush.
    },

    dispose() {
      handlers.clear();
    },

    getConnectionStatus() {
      return 'local';
    },
  };
};
